import React, { useContext, useMemo, useState, useCallback } from 'react';
import { FiHeart, FiEye } from "react-icons/fi";
import { MdAddShoppingCart } from "react-icons/md";
import { t } from 'i18n';
import { DataContext } from 'App';
import CartService from '../../services/cart';
import WishlistService from '../../services/wishlist';
import AuthService from '../../services/auth';
import { handleApiError, handleApiSuccess } from '../Handle';
import { Link } from 'react-router-dom';
import SubscriptionModal from "components/Subscriptions/SubscriptionModal";
import { formatPrice } from "utils/formatPrice";
import { getPrimaryProductImageSrc } from "../../utils/productImages";
import MagneticButton from "../cinema/MagneticButton";
import {
  PRODUCT_CARD_CART_CHROME,
  PRODUCT_CARD_CART_PING,
  PRODUCT_CARD_SUBSCRIBE_MOTION,
  PRODUCT_CARD_VIEW_MOTION,
  PRODUCT_CARD_WISHLIST_MOTION,
} from "utils/productCardMotion";
import { productPath } from 'utils/storePaths';

/**
 * Product actions & motion
 *
 * Primary CTAs (floating add-to-cart, bottom “View product” / “Subscribe”) are wrapped in
 * `MagneticButton` from `components/cinema`: tween-only hover/tap (ease [0.22, 1, 0.36, 1]),
 * optional damped magnetic pull on desktop pointer hover, disabled on touch + `prefers-reduced-motion`
 * inside `useMagneticEffect`. Do not stack extra `scale-*` on the same control — motion lives on the wrapper.
 * Card chrome (image zoom, card lift) stays CSS for performance; keep business logic outside wrappers.
 * Orange cart FAB uses `utils/productCardMotion` — soft ping ring appears on card hover (`group-hover`), like the chat widget.
 */

function StarRatingReadonly({ value }) {
  const v = Math.min(5, Math.max(0, Number(value) || 0));
  const pct = (v / 5) * 100;
  return (
    <div
      className="relative inline-block h-4 w-[4.5rem] text-[13px] leading-none"
      role="img"
      aria-label={t('product.ratingLabel', { value: v })}
    >
      <div className="pointer-events-none absolute inset-0 flex text-gray-200">
        {[1, 2, 3, 4, 5].map((i) => (
          <span key={i} className="w-1/5 text-center">★</span>
        ))}
      </div>
      <div className="pointer-events-none absolute start-0 top-0 h-full overflow-hidden text-amber-400" style={{ width: `${pct}%` }}>
        <div className="flex w-[4.5rem]">
          {[1, 2, 3, 4, 5].map((i) => (
            <span key={i} className="w-1/5 shrink-0 text-center">★</span>
          ))}
        </div>
      </div>
    </div>
  );
}

const EMPTY_PRODUCT = {};

const ProductCard = ({ data, sty }) => {
  const { wishList, setWishList, cartList, setCartList, toast, setSnackbar } = useContext(DataContext);

  const [cartLoading, setCartLoading] = useState(false);
  const [cartAdded, setCartAdded] = useState(false);
  const [wishLoading, setWishLoading] = useState(false);
  const [subOpen, setSubOpen] = useState(false);

  const product = useMemo(() => data || EMPTY_PRODUCT, [data]);
  const hasValidProduct = Boolean(product.id && (product.title || product.name));

  const wishlistItem = useMemo(() => {
    if (!hasValidProduct) return null;
    return wishList.find((item) => item.product?.id === product.id) || null;
  }, [hasValidProduct, wishList, product.id]);
  const isWished = Boolean(wishlistItem);

  const toggleWishlist = useCallback(async () => {
    if (!hasValidProduct) return;
    if (wishLoading) return;

    setWishLoading(true);
    try {
      if (!AuthService.isAuthenticated()) {
        if (isWished) {
          setWishList((prev) => {
            const next = prev.filter((item) => item.product?.id !== product.id);
            WishlistService.saveLocalWishlist(next);
            return next;
          });
          (toast?.success || ((msg) => setSnackbar({ ...handleApiSuccess(msg), open: true })))(t('productPage.removed'));
        } else {
          setWishList((prev) => {
            const next = [...prev, { product }];
            WishlistService.saveLocalWishlist(next);
            return next;
          });
          (toast?.success || ((msg) => setSnackbar({ ...handleApiSuccess(msg), open: true })))(`${t('productPage.saved')} ❤️`);
        }
        return;
      }

      if (wishlistItem) {
        const result = await WishlistService.removeFromWishlist(wishlistItem.id);
        if (result.success) {
          setWishList((prev) => prev.filter((item) => item.product?.id !== product.id));
          (toast?.success || ((msg) => setSnackbar({ ...handleApiSuccess(msg), open: true })))(t('productPage.removed'));
        } else {
          (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(result.error || t('productPage.wrong'));
        }
      } else {
        const result = await WishlistService.addToWishlist(product.id);
        if (result.success) {
          setWishList((prev) => [...prev, result.wishlistItem]);
          (toast?.success || ((msg) => setSnackbar({ ...handleApiSuccess(msg), open: true })))(`${t('productPage.saved')} ❤️`);
        } else {
          (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(result.error || t('productPage.wrong'));
        }
      }
    } catch (error) {
      console.error('Wishlist toggle error:', error);
      (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(t('productPage.wrong'));
    } finally {
      setWishLoading(false);
    }
  }, [hasValidProduct, wishLoading, isWished, product, wishlistItem, setWishList, setSnackbar, toast]);

  const addToCart = useCallback(async () => {
    if (cartLoading || !hasValidProduct) return;

    if (!AuthService.isAuthenticated()) {
      (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(t('productPage.loginForCart'));
      return;
    }

    if (!product.id) {
      (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(t('productPage.wrong'));
      return;
    }

    setCartLoading(true);
    const existingItem = cartList.find((item) => item.product?.id === product.id || item.product_id === product.id);
    let result;

    if (existingItem) {
      result = await CartService.updateCartItem(existingItem.id, existingItem.quantity + 1);
    } else {
      result = await CartService.addToCart(product.id, 1, product);
    }

    if (result.success) {
      if (AuthService.isAuthenticated()) {
        const cartResult = await CartService.getCart();
        if (cartResult.success) setCartList(cartResult.cartItems);
      } else {
        setCartList(CartService.loadLocalCart());
      }
      (toast?.success || ((msg) => setSnackbar({ ...handleApiSuccess(msg), open: true })))(
        existingItem ? t('productPage.qtyUpdated') : `${t('productPage.added')} ✅`
      );
      setCartAdded(true);
      setTimeout(() => setCartAdded(false), 1800);
    } else {
      (toast?.error || ((msg) => setSnackbar({ ...handleApiError(msg), open: true })))(result.error || t('productPage.wrong'));
    }
    setCartLoading(false);
  }, [cartLoading, hasValidProduct, product, cartList, setCartList, setSnackbar, toast]);

  if (!hasValidProduct) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 h-72 text-gray-400 text-sm">
        {t('product.noData')}
      </div>
    );
  }

  const productLink = productPath(product);

  /* ── derived display values ─────────────────────────────── */
  const savingsAmount =
    product.original_price && product.original_price > product.price
      ? (Number(product.original_price) - Number(product.price)).toFixed(2)
      : null;

  return (
    <div
      className="group relative flex h-full min-h-0 w-full flex-col rounded-2xl border border-gray-100 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)]"
      style={{ width: '100%', ...sty }}
    >
      <div className="relative h-64 w-full shrink-0 overflow-hidden rounded-t-2xl bg-gray-50">
        <img
          src={getPrimaryProductImageSrc(product)}
          alt={product.title || product.name || t('product.product')}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={(e) => {
            // Prevent infinite loop
            if (e.currentTarget.dataset.fallbackApplied === "1") return;
            e.currentTarget.dataset.fallbackApplied = "1";
            e.currentTarget.src = "/images/no-image.svg";
          }}
          className="h-full w-full object-contain p-3 transition-transform duration-500 ease-out group-hover:scale-[1.02]"
        />

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/20 to-transparent" />

        <div className="absolute top-3 end-3 z-10 flex flex-col gap-2">
          <button
            type="button"
            onClick={toggleWishlist}
            disabled={wishLoading}
            aria-label={isWished ? t('product.removeFromWishlist', { name: product.title || product.name }) : t('product.saveToWishlist', { name: product.title || product.name })}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-1 ${PRODUCT_CARD_WISHLIST_MOTION} ${isWished ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-white/90 text-gray-500 hover:bg-white hover:text-red-400'}`}
          >
            {wishLoading ? (
              <svg className="h-4 w-4 animate-spin text-current" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : (
              <FiHeart className={`h-4 w-4 transition-transform duration-150 ${isWished ? 'scale-110 fill-current' : 'scale-100'}`} />
            )}
          </button>
        </div>

        <div className="absolute bottom-3 end-3 z-10">
          <MagneticButton glow={false} strength={0.22}>
            <div className="relative inline-flex">
              {!cartLoading && !cartAdded ? (
                <span className={PRODUCT_CARD_CART_PING} aria-hidden />
              ) : null}
              <button
                type="button"
                onClick={addToCart}
                disabled={cartLoading}
                aria-label={t('product.addToCart')}
                className={`relative z-10 inline-flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${PRODUCT_CARD_CART_CHROME} ${cartAdded ? 'bg-emerald-500 text-white shadow-[0_4px_14px_rgba(16,185,129,0.35)]' : cartLoading ? 'cursor-wait bg-primary/60 text-on-accent' : 'bg-gradient-to-r from-primary to-primary-hover text-on-accent hover:from-primary-hover hover:to-primary-hover'}`}
              >
              {cartLoading ? (
                <svg className="h-4 w-4 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              ) : cartAdded ? (
                <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <MdAddShoppingCart className="h-4 w-4 shrink-0" />
              )}
            </button>
            </div>
          </MagneticButton>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-4">
        <div className="flex flex-1 flex-col gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-primary">
            {product.category || t('product.generalCategory')}
          </p>

          <Link
            to={productLink}
            className="line-clamp-2 text-sm font-semibold leading-snug text-gray-900 transition-colors duration-200 hover:text-primary"
          >
            {product.title || product.name || t('product.untitled')}
          </Link>

          <div className="flex items-center gap-1.5">
            <StarRatingReadonly value={product.rating || 4.5} />
            <span className="text-xs text-gray-500">{t('product.reviewCount', { count: product.review_count || 0 })}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-bold text-gray-900" data-testid="card-price">
              {product.has_variants && product.price_max
                ? t('variants.fromPrice', { price: formatPrice(product.price) })
                : formatPrice(product.price)}
            </span>
            {savingsAmount && (
              <>
                <span className="text-sm text-gray-500 line-through">{formatPrice(product.original_price)}</span>
                <span className="text-sm font-medium text-green-600">{t('product.save', { amount: formatPrice(savingsAmount) })}</span>
              </>
            )}
          </div>
          {Array.isArray(product.color_swatches) && product.color_swatches.length > 0 && (
            <div className="flex items-center gap-1" data-testid="card-colour-dots" aria-label={t('variants.colours', { count: product.color_swatches.length })}>
              {product.color_swatches.slice(0, 5).map((s) => (
                <span
                  key={s.value}
                  title={s.value}
                  className="h-3.5 w-3.5 rounded-full border border-black/15 bg-cover bg-center"
                  style={s.image ? { backgroundImage: `url(${s.image})` } : { backgroundColor: s.hex || '#e2e8f0' }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-auto flex min-w-0 items-center gap-2 pt-3">
          <MagneticButton glow={false} strength={0.18} className="min-w-0 flex-1">
            <Link
              to={productLink}
              className={`inline-flex w-full min-w-0 items-center justify-center gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-medium sm:px-4 text-gray-700 transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-primary/50 hover:bg-amber-50 hover:text-amber-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${PRODUCT_CARD_VIEW_MOTION}`}
            >
              <FiEye className="h-4 w-4 shrink-0" />
              {t('product.view')}
            </Link>
          </MagneticButton>
          <MagneticButton glow={false} strength={0.18} className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setSubOpen(true)}
              className={`inline-flex w-full min-w-0 items-center justify-center rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-medium sm:px-4 text-gray-900 transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${PRODUCT_CARD_SUBSCRIBE_MOTION}`}
            >
              {t('product.subscribe')}
            </button>
          </MagneticButton>
        </div>
      </div>

      <SubscriptionModal
        open={subOpen}
        onClose={() => setSubOpen(false)}
        mode="grocery"
        presetProduct={hasValidProduct ? product : null}
        onCreated={() => setSubOpen(false)}
      />
    </div>
  );
};

export default ProductCard;
